import React from 'react';
import { withNamespaces, Trans } from 'react-i18next';
import { observer, inject } from 'mobx-react';
import styled from 'styled-components';

import PropTypes from 'prop-types';

import { Link } from 'core/routing';

import { Editor } from 'libs/editor';
import { html } from 'libs/editor/serializers/html';
import { EditorStore } from 'libs/editor/store';

import { MainWrapper, Center, Divider } from 'legacyComponents/styled/lib';
import { Avatar } from 'apps/components/shared';
import { Button, Spinner, Heading } from 'components';
import Header from 'apps/components/header/header';
import { headerStore } from 'apps/components/header/app';
import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import { Description as DescriptionBase } from 'apps/initiative/views/intro';
import { SignInForm } from 'apps/signIn/views/signInForm';
import { ResetPasswordModal } from 'apps/signIn/resetPassword/index';
import { conf as mainConf } from 'apps/config.yaml';
import { imageSrc } from 'apps/file/utils';

import { InviteAppManager } from 'apps/home/invite/appManager';

@withNamespaces()
@inject('routerStore')
@observer
export class InvitePage extends React.Component {
  UNSAFE_componentWillMount() {
    this.appManager = new InviteAppManager(this, this.props);
    if (!clientStore.signedInProfile) {
      clientStore.routerStore.navigate('home.invite', {
        inviteId: this.props.params.get('inviteId'),
        referrerUserId: this.props.params.get('referrerUserId'),
        token: this.props.params.get('token'),
        next: clientStore.next(),
      });
      this.signInManager = clientStore.context.get('signInAppManager');
    }
  }

  clickNoAccount() {
    clientStore.routerStore.navigate('home.signUp', {
      next: clientStore.next(),
    });
  }

  clickForgotPassword() {
    modalStore.SetModalComponent(ResetPasswordModal);
  }

  componentDidMount() {
    // when user is not login, then save the invite link to session storage
    if (!clientStore.signedInProfile) {
      sessionStorage.setItem('inviteLink', window.location.href);
    }
  }

  render() {
    const { loading, invite, Accept, Reject } = this.appManager;
    if (loading) {
      return (
        <Center>
          <Spinner margin='200px auto' />;
        </Center>
      );
    }
    if (invite === null) {
      return (
        <>
          <Header clientStore={clientStore} headerStore={headerStore} />
          <MainWrapper marginTop='90px' marginBottom='90px'>
            <Center>
              <Heading level={2}>
                <Trans>Hmm... Looks like this invite link has expired.</Trans>
              </Heading>
            </Center>
            <Center>
              <Link name='home'>
                <Button outline>
                  <Trans>Back Home</Trans>
                </Button>
              </Link>
            </Center>
          </MainWrapper>
        </>
      );
    }
    const {
      content: {
        title,
        description,
        avatar,
        imageFallbackPath,
        type,
        isPrivate,
      },
    } = invite;

    let preview;
    if (type === 'initiative') {
      preview = (
        <>
          <Center>
            <Avatar
              src={imageSrc(
                avatar,
                imageFallbackPath,
                mainConf.imgSizes.profile.large,
              )}
              size='80px'
              noOpacity={true}
            />
          </Center>
          <Center>
            <Title>
              <Trans>{title}</Trans>
            </Title>
          </Center>
          <Description center>{description}</Description>
          <Divider width='100%' />
        </>
      );
    } else {
      const store = new EditorStore({
        initialValue: html.deserialize(description || '<p></p>'),
        formatting: 'advanced',
      });
      preview = (
        <>
          {isPrivate ? null : (
            <Img
              src={imageSrc(
                avatar,
                imageFallbackPath,
                mainConf.imgSizes.profile.large,
              )}
            />
          )}
          <Title noMarginTop={isPrivate}>
            {title}{' '}
            {isPrivate ? (
              <Private>
                <Trans>(Private)</Trans>
              </Private>
            ) : null}
          </Title>
          <EditorWrapper>
            <Editor store={store} />
          </EditorWrapper>
          <Divider width='100%' />
        </>
      );
    }

    let actions = (
      <Center>
        <ButtonStyled
          text={<Trans>Reject invite</Trans>}
          onClick={() => Reject()}
          variant='destructive'
        />
        <ButtonStyled
          text={<Trans>Accept invite</Trans>}
          onClick={() => Accept()}
          outline
          data-testid='accept-invite-button'
        />
      </Center>
    );
    if (!clientStore.signedInProfile) {
      actions = (
        <SignInWrapper>
          <SignInForm
            title='Sign in/up to accept this invitation'
            form={this.signInManager.signInApp.form}
            handleForgotPassword={this.clickForgotPassword}
            handleSignIn={this.signInManager.signInApp.SignIn}
            handleNoAccount={this.clickNoAccount}
          />
        </SignInWrapper>
      );
    }

    return (
      <>
        <Header clientStore={clientStore} headerStore={headerStore} />
        <MainWrapper marginTop='90px' marginBottom='90px'>
          <Center>
            <Heading level={2}>
              <Trans>You&apos;ve been invited to join...</Trans>
            </Heading>
          </Center>
          <InvWrapper>
            {preview}
            {actions}
          </InvWrapper>
        </MainWrapper>
      </>
    );
  }
}

InvitePage.propTypes = {
  params: PropTypes.object,
};

const InvWrapper = styled.div`
  width: 700px;
  margin: 20px auto;
  padding: 30px 30px 0 30px;
  height: auto;
  border: 1px solid ${props => props.theme.color.wfGrey200};
`;

const Title = styled.h1`
  margin: ${props => (props.noMarginTop ? '0 0 15px 0' : '20px 0 15px 0')};
`;

const Private = styled.span`
  opacity: 0.75;
  font-size: 0.8em;
`;

const Description = styled(DescriptionBase)`
  margin-bottom: 20px;
`;

const Img = styled.img`
  width: 100%;
  height: auto;
  border: 1px solid ${props => props.theme.color.wfGrey200};
`;

const SignInWrapper = styled.div`
  margin: 10px 0 0 0;
`;

const EditorWrapper = styled.div`
  padding: 0 0 20px 0;
  color: ${props => props.theme.color.wfGrey900};
  font-size: 17px;
  line-height: 1.6;
`;

const ButtonStyled = styled(Button)`
  margin: ${props =>
    `${props.theme.spacing.large} ${props.theme.spacing.small}`};
`;
